import { useStore } from 'effector-react'
import { ParseKeys } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  $inspectionReasonOptions,
  $lastInspection,
  $savingPhotosCount,
  requestInspectionReasonOptionsFx,
  savePhoto,
} from '~/entities/Inspection/model'
import {
  validateCarPhotos,
  validateMileage,
} from '~/entities/Inspection/ui/SpecificationsForm/helpers'
import { InspectionFetchOption, RepairOrderAttributes } from '~/shared/api'
import { incidentResponsibleEnumOptions } from '~/shared/config/constants'
import {
  IncidentResponsibleEnum,
  InspectionPhotoTypeEnum,
  InspectionReasonCodeEnum,
} from '~/shared/config/enums'
import { formatDateForUI } from '~/shared/lib/date'
import { useParserI18nMessage } from '~/shared/lib/i18n'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { Button } from '~/shared/ui/Buttons'
import {
  DrawerSelect,
  NumericInput,
  Camera,
  FormProvider,
  Switch,
} from '~/shared/ui/form'
import { RadioGroup } from '~/shared/ui/form/RadioGroup'
import { FormHeader } from '../FormHeader'
import { helpersCarPhotosCamera } from './constants'
import {
  LatestRepairOrderDescription,
  MessageError,
  ParamsWrapper,
  Wrapper,
} from './styled'
import { SpecificationsFormValues } from './types'

type SpecificationsFormProps = {
  onSuccess: () => void
  form: UseFormReturn<SpecificationsFormValues>
  latestRepairOrder?: RepairOrderAttributes
}

export function SpecificationsForm({
  onSuccess,
  form,
  latestRepairOrder,
}: SpecificationsFormProps) {
  const { t } = useTranslation()
  const { parserI18nMessage } = useParserI18nMessage()

  const reasonOptionsLoading = useStore(
    requestInspectionReasonOptionsFx.pending,
  )
  const reasonOptions = useStore($inspectionReasonOptions)

  const savingCarPhotoCount =
    useStore($savingPhotosCount)[InspectionPhotoTypeEnum.CAR]
  const isCarPhotoPending = savingCarPhotoCount > 0

  const savingMileagePhotoCount =
    useStore($savingPhotosCount)[InspectionPhotoTypeEnum.MILEAGE]
  const isMileagePhotoPending = savingMileagePhotoCount > 0

  const lastInspection = useStore($lastInspection)
  const lastInspectionMileage = useMemo(
    () => lastInspection?.getMileage() || 0,
    [lastInspection],
  )

  const [mileageError, setMileageError] = useState<string>()
  const [isCarPhotosError, setIsCarPhotosError] = useState<boolean>()

  const { watch, setValue } = form

  const reasonOptionCode = watch('reasonOption')?.code
  const carPhotos = watch('carPhotos')

  const showWithDriverParticipationParams =
    reasonOptionCode !== InspectionReasonCodeEnum.INITIAL &&
    reasonOptionCode !== InspectionReasonCodeEnum.CONFISCATION

  const isAfterAccident =
    reasonOptionCode === InspectionReasonCodeEnum.AFTER_ACCIDENT

  const isPlanOrRefund =
    reasonOptionCode === InspectionReasonCodeEnum.PLAN ||
    reasonOptionCode === InspectionReasonCodeEnum.REFUND

  const isInitialOrRefund =
    reasonOptionCode === InspectionReasonCodeEnum.INITIAL ||
    reasonOptionCode === InspectionReasonCodeEnum.REFUND

  useEffect(() => {
    // Для автомобилей без осмотров по дефолту INITIAL
    if (!lastInspection && reasonOptions?.length) {
      setValue(
        'reasonOption',
        reasonOptions.find(
          (option) => option.code === InspectionReasonCodeEnum.INITIAL,
        ) as InspectionFetchOption,
      )
    }
    // eslint-disable-next-line
  }, [reasonOptions])

  watch(async (data, { name, type }) => {
    if (type !== 'change') return

    if (name === 'reasonOption') {
      setValue(
        'incidentResponsible',
        data[name]?.code === InspectionReasonCodeEnum.AFTER_ACCIDENT
          ? IncidentResponsibleEnum.NOT_DRIVER
          : undefined,
      )
      setValue('withDriverParticipation', false)
      setValue('isPlanned', false)
      setIsCarPhotosError(false)
    }
    if (name === 'mileage') {
      const messageError = validateMileage(data[name], lastInspectionMileage)
      setMileageError(messageError)
    }
  })

  const mappedParams = useMemo(() => {
    return [
      { name: 'isExteriorClean', label: 'inspectionParams.clean car' },
      { name: 'isCarRefueled', label: 'inspectionParams.car is fueled' },
      showWithDriverParticipationParams
        ? {
            name: 'withDriverParticipation',
            label: 'inspectionParams.driver is present for inspection',
          }
        : null,
      isPlanOrRefund
        ? {
            name: 'isPlanned',
            label: 'inspectionParams.inspection by appointment',
          }
        : null,
    ].filter(Boolean) as { name: string; label: ParseKeys }[]
  }, [isPlanOrRefund, showWithDriverParticipationParams])

  const handleOnSuccess = () => {
    if (mileageError) {
      snackbarEnqueued({
        messageI18n:
          'validate.to proceed to the next step, please enter a valid mileage!',
        variant: 'warning',
      })
      return
    }

    const carPhotosError = validateCarPhotos(isInitialOrRefund, carPhotos)
    setIsCarPhotosError(carPhotosError)
    if (carPhotosError) {
      snackbarEnqueued({
        messageI18n: 'validate.to proceed to the next step, attach 9 photos',
        variant: 'warning',
      })
      return
    }
    onSuccess()
  }

  return (
    <FormProvider form={form} onSuccess={handleOnSuccess}>
      <Wrapper>
        <div>
          <NumericInput label='form.enter mileage' name='mileage' />
          {mileageError && (
            <MessageError>{parserI18nMessage(mileageError)}</MessageError>
          )}
        </div>

        {latestRepairOrder && (
          <LatestRepairOrderDescription>
            {t('alert.last service date; mileage KM', {
              date: formatDateForUI(latestRepairOrder.createdAt),
              mileage: latestRepairOrder?.mileage.toLocaleString(),
            })}
          </LatestRepairOrderDescription>
        )}

        <Camera
          buttonTitle='form.mileage photo'
          name='mileagePhotos'
          saveFileFn={(file) =>
            savePhoto({
              file,
              type: InspectionPhotoTypeEnum.MILEAGE,
              form,
              fieldName: 'mileagePhotos',
            })
          }
          isLoading={isMileagePhotoPending}
        />

        <DrawerSelect
          label='form.inspection reason'
          name='reasonOption'
          defaultOptions={reasonOptions}
          loading={reasonOptionsLoading}
          disabled={!lastInspection}
          title='form.inspection reason'
          dataTestId='inspection-reason-drawer'
        />

        <div>
          <Camera
            buttonTitle='form.add car photo'
            name='carPhotos'
            helpers={helpersCarPhotosCamera}
            saveFileFn={(file) =>
              savePhoto({
                file,
                type: InspectionPhotoTypeEnum.CAR,
                form,
                fieldName: 'carPhotos',
              })
            }
            isLoading={isCarPhotoPending}
          />

          {isCarPhotosError && (
            <MessageError>{t('validate.add at least 9 photos')}</MessageError>
          )}
        </div>

        {isAfterAccident && (
          <>
            <FormHeader title='form.accident culprit' />
            <ParamsWrapper>
              <RadioGroup
                name='incidentResponsible'
                options={incidentResponsibleEnumOptions}
              />
            </ParamsWrapper>
          </>
        )}

        <ParamsWrapper>
          {mappedParams.map((param) => (
            <Switch
              key={param.name}
              labelI18n={param.label}
              name={param.name}
            />
          ))}
        </ParamsWrapper>

        <Button type='submit' fullWidth uppercase>
          {t('button.next')}
        </Button>
      </Wrapper>
    </FormProvider>
  )
}
